import React, { useState } from 'react';
import { ButtonStyled, ModalActionsStyled, TextStyled } from '@nike/nike-design-system-components';
import clsx from 'clsx';
import { useModal } from '@nike/podium-dialog-manager';
import { useAnalyticsContext } from '../../context/analyticsContext';
import { SQCookie } from '../../../utils/legacyV2Cookies';
import cookieChecks from './cookie-checks';
import CookieCheckbox from './CookieCheckbox';
import {
  CookieModalStyled,
  FooterSeparator,
  ThreeButtonModalActions,
  TwoButtonModalActions,
  backdropContainer,
  cookieModalDoneButton,
  ncss4Dot2textColorFix,
} from './styles';
import {
  useBodegaStrings,
  useIsRejectButtonEnabled,
  usePreferences,
  useShouldShowCookieModal,
} from './helpers';
import useBodega from '../../utils/hooks/bodegaHook';
import { BODEGA_DEFAULTS, NAV_CUSTOM_EVENTS } from '../../constants';
import { useCreateEventListener } from '../../utils/hooks/createEvent';

const CookieModal = () => {
  const [preferences, setPreferences] = usePreferences();
  const { newRelic } = useAnalyticsContext();
  // true sets priority to high
  const { isOpen, closeModal, openModal } = useModal(true);
  useShouldShowCookieModal(openModal);
  useCreateEventListener(NAV_CUSTOM_EVENTS.OPEN_COOKIE_MODAL, openModal);
  const [isExpanded, setIsExpanded] = useState(false);
  const isRejectButtonEnabled = useIsRejectButtonEnabled();
  const closeModalLabel = useBodega('nav_close_modal', BODEGA_DEFAULTS.CLOSE_MODAL);
  const {
    title,
    acceptButtonText,
    rejectButtonText,
    moreInformationButtonText,
    doneButtonText,
    policyText,
    footerText,
  } = useBodegaStrings();

  const acceptAll = () => {
    SQCookie.acceptAll();
    newRelic?.log({ ...SQCookie.get() }, 'cookie-modal-simple-accept-clicked');
    SQCookie.get();
    closeModal();
  };

  const rejectAll = () => {
    SQCookie.rejectAll();
    newRelic?.log({ ...SQCookie.get() }, `cookie-modal-simple-reject-clicked`);
    closeModal();
  };

  const handleDoneClick = () => {
    SQCookie.set(preferences);
    newRelic?.log({ ...SQCookie.get() }, 'cookie-modal-expanded-done-clicked');
    closeModal();
  };

  const handleCheckboxChange = name => {
    setPreferences(prevChecks => ({ ...prevChecks, [name]: !prevChecks[name] }));
  };

  const acceptButton = (
    <ButtonStyled appearance="primary" data-testid="accept-cookies-button" onClick={acceptAll}>
      {acceptButtonText}
    </ButtonStyled>
  );
  const rejectButton = (
    <ButtonStyled appearance="primary" data-testid="reject-cookies-button" onClick={rejectAll}>
      {rejectButtonText}
    </ButtonStyled>
  );
  const moreInformationButton = (
    <ButtonStyled
      appearance={isRejectButtonEnabled ? 'primary' : 'secondary'}
      className={clsx(!isRejectButtonEnabled && ncss4Dot2textColorFix)}
      data-testid="more-information-cookies-button"
      onClick={() => setIsExpanded(true)}
    >
      {moreInformationButtonText}
    </ButtonStyled>
  );
  const simpleModalActions = isRejectButtonEnabled ? (
    <ThreeButtonModalActions classes={{ childrenWrapper: 'cookie-modal-actions' }}>
      {acceptButton}
      {rejectButton}
      {moreInformationButton}
    </ThreeButtonModalActions>
  ) : (
    <TwoButtonModalActions classes={{ childrenWrapper: 'cookie-modal-actions' }}>
      {moreInformationButton}
      {acceptButton}
    </TwoButtonModalActions>
  );

  const modalActions = isExpanded ? (
    <ModalActionsStyled>
      <ButtonStyled
        appearance="primary"
        className={cookieModalDoneButton}
        onClick={handleDoneClick}
      >
        {doneButtonText}
      </ButtonStyled>
    </ModalActionsStyled>
  ) : (
    simpleModalActions
  );

  const modalContent = isExpanded ? (
    cookieChecks.map(check => (
      <CookieCheckbox
        isChecked={check.readOnly || preferences[check.name]}
        key={`cookie-checkbox-${check.name}`}
        onChange={handleCheckboxChange}
        {...check}
      />
    ))
  ) : (
    <TextStyled appearance="legal">{policyText}</TextStyled>
  );

  const FooterSlot = () => (
    <>
      {modalActions}
      <FooterSeparator />
      <TextStyled appearance="legal" data-testid="cookie-modal-footer-text">
        {footerText}
      </TextStyled>
    </>
  );

  return (
    <CookieModalStyled
      appearance={isExpanded ? 'baseMedium' : 'base'}
      classes={{
        backdropContainer: backdropContainer,
      }}
      isOpen={isOpen}
      modalCloseAriaLabel={closeModalLabel}
      shouldCloseOnBackgroundClick={false}
      shouldCloseOnEscape={false}
      slots={{ FooterSlot: <FooterSlot /> }}
      title={title}
      titleId={isExpanded ? 'cookie-modal-expanded' : 'cookie-modal-simple'}
    >
      {modalContent}
    </CookieModalStyled>
  );
};

export default CookieModal;
